// localStorage utility functions with error handling

/**
 * Safely get item from localStorage
 * @param {string} key - The localStorage key
 * @returns {string|null} The stored value or null
 */
export const getLocalStorageItem = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.warn(`Failed to read from localStorage (${key}):`, error);
    return null;
  }
};

/**
 * Safely set item in localStorage
 * @param {string} key - The localStorage key
 * @param {string} value - The value to store
 * @returns {boolean} Success status
 */
export const setLocalStorageItem = (key, value) => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    console.warn(`Failed to write to localStorage (${key}):`, error);
    return false;
  }
};

/**
 * Safely remove item from localStorage
 * @param {string} key - The localStorage key
 * @returns {boolean} Success status
 */
export const removeLocalStorageItem = (key) => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.warn(`Failed to remove from localStorage (${key}):`, error);
    return false;
  }
};

/**
 * Get cached predictions with validation
 * @param {string} cacheKey - Cache data key
 * @param {string} timestampKey - Cache timestamp key
 * @param {number} validityHours - Cache validity in hours
 * @returns {Object|null} Parsed cache data or null
 */
export const getCachedPredictions = (cacheKey, timestampKey, validityHours) => {
  const cachedData = getLocalStorageItem(cacheKey);
  const cachedTimestamp = getLocalStorageItem(timestampKey);
  
  if (!cachedData || !cachedTimestamp) {
    return null;
  }
  
  // Check cache validity
  const cacheAge = Date.now() - parseInt(cachedTimestamp);
  const cacheValid = cacheAge < validityHours * 60 * 60 * 1000;
  
  if (!cacheValid) {
    return null;
  }
  
  try {
    return JSON.parse(cachedData);
  } catch (error) {
    console.warn('Failed to parse cached predictions:', error);
    return null;
  }
};

/**
 * Save predictions to cache
 * @param {string} cacheKey - Cache data key
 * @param {string} timestampKey - Cache timestamp key
 * @param {Array} data - Data to cache
 * @returns {boolean} Success status
 */
export const cachePredictions = (cacheKey, timestampKey, data) => {
  const dataSuccess = setLocalStorageItem(cacheKey, JSON.stringify(data));
  const timestampSuccess = setLocalStorageItem(timestampKey, Date.now().toString());
  return dataSuccess && timestampSuccess;
};

